<script lang="ts">
  import { fade } from "svelte/transition";
  import { derived, get, writable } from "svelte/store";
  import { __DEV__ } from "../helpers/constants";
  import {
    createColumnHelper,
    createSvelteTable,
    getCoreRowModel,
    type Cell,
    type Header,
    type ColumnDef,
    type ColumnSort,
    type Updater,
    getSortedRowModel,
  } from "@tanstack/svelte-table";
  import { globalDatePickerStore } from "../stores/date-picker";
  import { createPriceHistoryQuery } from "../queries/price-history";
  import {
    CELL_FADE_BASE_ANIM_CONFIG,
    CHAT_FILTER_PARAM_KEY,
    COL_HEADER_HEIGHT,
    GRID_WIDTH,
    PRICE_HISTORY_CHAT_FILTER_STORAGE_KEY,
    PRICE_HISTORY_CHAT_FILTER_VERSION,
    SCROLL_BAR_WIDTH,
  } from "../components/invoices/constants";

  import {
    DateFilterType,
    type PricesDateEntry,
    type PricesInvoiceProductPrice,
  } from "../helpers/types";
  import { tableOptions } from "../helpers/table";
  import { sideMenuWidth } from "../components/side-menu/SideMenu.svelte";
  import UploadInvoiceEmptyState from "../components/empty-states/UploadInvoiceEmptyState.svelte";
  import TextNew from "../components/TextNew.svelte";
  import DatePickerPanel from "../components/date-picker/DatePickerPanel.svelte";
  import EmptyStateBasic from "../components/empty-states/EmptyStateBasic.svelte";
  import PriceHistoryCell from "../components/prices/PriceHistoryCell.svelte";
  import HeaderCell from "../components/invoices/HeaderCell.svelte";
  import SkeletonColHeader from "../components/invoices/SkeletonColHeader.svelte";
  import PriceHistoryRowSkeleton from "../components/skeletons/PriceHistoryRowSkeleton.svelte";
  import HeaderCellFilter from "../components/invoices/HeaderCellFilter.svelte";
  import { createDropdownMenu } from "../components/dropdown-menu/create";
  import { createPersistedLocalStore } from "../stores/persisted-local-store";
  import { onDestroy, onMount } from "svelte";
  import { restorePersistedChatFilters } from "../helpers/gp";
  import { querystring, location } from "svelte-spa-router";
  import {
    currencyFormatter,
    currencySymbol,
    formatDayMonthByLocale,
    locale,
  } from "../helpers/localise";
  import {
    createChatFilterMenuItems,
    syncQueryParamsWithChatFilters,
  } from "../components/dropdown-menu/chat-filter-menu";
  import difference from "lodash/difference";
  import { createSuppliersChatsByBuyerIDsQuery } from "../queries/suppliers";
  import { t } from "../helpers/translations";
  import PageHeaderNew from "../components/headers/PageHeaderNew.svelte";
  import ButtonGroup from "../components/spend-report/ButtonGroup.svelte";
  import RadioButton from "../components/RadioButton.svelte";
  import PriceChangesEmailModal from "../components/prices/PriceChangesEmailModal.svelte";
  import { createModal } from "../components/modals/createModal";
  import ButtonNew from "../components/buttons/ButtonNew.svelte";
  import { selectedBuyer } from "../stores/buyer";
  import { createExportCSVPriceHistoryMutation } from "../mutations/price-history";
  import type { ExportPriceHistoryCSVParams } from "../api/price-history";

  type PriceHistoryRow = {
    supplierName: string;
    supplierLogo: string;
    dates: PricesDateEntry[];
    name: string;
    unit: string;
    productCode: string;
    periodSpend: number;
    prices: PricesInvoiceProductPrice[];
    initialPrice: PricesInvoiceProductPrice;
    latestPrice: PricesInvoiceProductPrice;
    increase: boolean;
    hasChange: boolean;
  };

  let hasRestoredFilters = false;
  let isSupplierChatFilterTooltipOpen = false;
  let tooltipTimeout: NodeJS.Timeout | number | null = null;

  const supplierFilterBadgeElement = writable<HTMLElement | null>(null);
  const chatFiltersByID = createPersistedLocalStore<Record<string, boolean>>(
    PRICE_HISTORY_CHAT_FILTER_STORAGE_KEY,
    {},
    PRICE_HISTORY_CHAT_FILTER_VERSION
  );
  const chatFilterCount = derived(chatFiltersByID, (s) => Object.values(s).filter(Boolean).length);

  const {
    open: chatFilterOpen,
    menu: chatFilterMenu,
    menuAttrs: chatFilterMenuAttrs,
    triggerAttrs: chatFilterTriggerAttrs,
    onTriggerClick: chatFilterOnTriggerClick,
    triggerElement: chatFilterTriggerElement,
  } = createDropdownMenu();

  const {
    contentAttrs,
    underlayAttrs,
    open: isPriceChangeEmailModalOpen,
    triggerElement,
    ...modal
  } = createModal();

  const dateRangeFilters = [
    DateFilterType.Past7Days,
    DateFilterType.Past14Days,
    DateFilterType.ThisMonth,
  ];

  $: if (hasRestoredFilters && $location === "/prices") {
    syncQueryParamsWithChatFilters($querystring, chatFiltersByID);
  }

  const columnWidthsByID = new Map<string, string>([
    ["supplierName", "2fr"],
    ["name", "2fr"],
    ["prices", "1.5fr"],
    ["initialPrice", "1fr"],
    ["latestPrice", "1fr"],
    ["hasChange", "2fr"],
  ]);

  $: columnWidths = Array.from(columnWidthsByID.values()).join(" ");

  const changesOnly = writable(true);

  const columnHelper = createColumnHelper<PriceHistoryRow>();

  const defaultColumns = [
    columnHelper.accessor("supplierName", {
      cell: (context) => context.getValue(),
      id: "supplierName",
      header: "supplier",
      enableSorting: true,
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("name", {
      cell: (context) => context.getValue(),
      id: "product",
      header: "product",
      enableSorting: false,
    }),
    columnHelper.accessor("periodSpend", {
      cell: (context) => {
        return $currencyFormatter.format(context.getValue() / 100);
      },
      id: "periodSpend",
      header: "period spend",
      sortingFn: "alphanumeric",
      enableSorting: true,
    }),
    columnHelper.accessor("initialPrice", {
      cell: (context) => {
        const price = context.getValue();
        return price.formattedUnitAmount;
      },
      id: "initialPrice",
      header: `initial ${$currencySymbol}`,
      enableSorting: false,
    }),
    columnHelper.accessor("latestPrice", {
      cell: (context) => {
        const price = context.getValue();
        return price.formattedUnitAmount;
      },
      id: "latestPrice",
      header: `current ${$currencySymbol}`,
      enableSorting: false,
    }),
    columnHelper.accessor("hasChange", {
      id: "hasChange",
      header: "price history",
      cell: (context) => {
        const row = context.row.original;
        return {
          hasChange: row.hasChange,
          lineColor: row.increase ? "#D11514" : "#006943",
          values: row.prices
            .filter((p) => p)
            .reverse() //Reverse array to show in chronological order
            .map((p) => {
              return {
                price: p.unitAmountCents / 100,
                formattedUnitAmount: p.formattedUnitAmount,
                date: new Date(p.invoiceDate),
              };
            }),
        };
      },
      enableSorting: false,
    }),
  ];

  const skeletonRows = new Array(9).fill(null);

  const sorting = writable<ColumnSort[]>([{ id: "periodSpend", desc: true }]);

  function setSorting(updater: Updater<ColumnSort[]>) {
    if (updater instanceof Function) {
      sorting.set(updater(get(sorting)));
    } else {
      sorting.set(updater);
    }
  }

  const priceHistoryParams = derived(
    [selectedBuyer, globalDatePickerStore, changesOnly, chatFiltersByID],
    ([$selectedBuyer, $datePickerStore, $changesOnly, $chatFiltersByID]) => {
      return {
        buyerId: $selectedBuyer?.id || "",
        startDate: $datePickerStore.confirmedStartDate.toISOString(),
        endDate: $datePickerStore.confirmedEndDate.toISOString(),
        changesOnly: $changesOnly,
        chatIDs: Object.entries($chatFiltersByID)
          .filter(([, selected]) => selected)
          .map(([chatID]) => chatID),
      };
    }
  );
  const priceHistoryUnfilteredParams = derived(priceHistoryParams, ($priceHistoryParams) => ({
    ...$priceHistoryParams,
    chatIDs: [],
  }));
  const priceHistoryUnfilteredQuery = createPriceHistoryQuery(priceHistoryUnfilteredParams);
  const priceHistoryQuery = createPriceHistoryQuery(priceHistoryParams);

  const exportCSVPriceHistoryMutation = createExportCSVPriceHistoryMutation();

  function handleExportCSVClick() {
    const sortingFilter = get(sorting);
    const sort: ExportPriceHistoryCSVParams["sort"] =
      sortingFilter.length > 0
        ? `period_spend_${sortingFilter[0].desc ? "desc" : "asc"}`
        : undefined;

    const params = {
      ...$priceHistoryParams,
      sort,
    };
    $exportCSVPriceHistoryMutation.mutate(params);
  }

  const supplierChatsQueryParams = derived(
    [selectedBuyer, chatFiltersByID, priceHistoryUnfilteredQuery],
    ([$selectedBuyer, $chatFiltersByID, $priceHistoryUnfilteredQuery]) => {
      return {
        buyerIDs: [$selectedBuyer?.id ?? ""],
        chatIDs: difference(
          Object.keys($chatFiltersByID),
          $priceHistoryUnfilteredQuery.data?.chats?.map((c) => c.chat_id) ?? []
        ),
      };
    }
  );
  const supplierChatsUnavailableForDateRange =
    createSuppliersChatsByBuyerIDsQuery(supplierChatsQueryParams);

  $: chatFilterMenuItems = createChatFilterMenuItems(
    $priceHistoryUnfilteredQuery.data?.chats,
    $chatFiltersByID,
    $supplierChatsUnavailableForDateRange.data
  );

  const tableData = derived([priceHistoryQuery, sorting], ([$priceHistoryQuery, _$sorting]) => {
    if (!$priceHistoryQuery.data) {
      return { data: [] };
    }

    let prices: PriceHistoryRow[] = [];
    $priceHistoryQuery.data?.history.forEach((supplier) => {
      supplier.products.forEach((product) => {
        const validPrices = product.prices.filter((x) => x);
        const initialPrice = validPrices[validPrices.length - 1];
        const latestPrice = validPrices[0];
        const increase = latestPrice.unitAmountCents > initialPrice.unitAmountCents;
        const hasChange = product.hasChange;

        prices = [
          ...prices,
          {
            supplierName: supplier.name,
            supplierLogo: supplier.supplierLogo,
            dates: supplier.dates,
            name: product.name,
            unit: product.unitName,
            prices: product.prices,
            periodSpend: product.periodSpend,
            initialPrice: initialPrice,
            latestPrice: latestPrice,
            productCode: product.productCode,
            increase,
            hasChange,
          },
        ];
      });
    });

    return { data: prices };
  });

  $: isEmptyData =
    !$priceHistoryQuery.isFetching && $priceHistoryQuery.isSuccess && $tableData.data.length === 0;

  const table = createSvelteTable(
    derived([tableData, sorting], ([$tableData, $sorting]) =>
      tableOptions({
        data: $tableData.data,
        columns: defaultColumns,
        state: {
          sorting: $sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: (row, index) => row.name + index,
        debugColumns: __DEV__,
        debugTable: __DEV__,
        debugHeaders: __DEV__,
      })
    )
  );

  $: rows = $table.getRowModel().rows;

  function renderHeaderValue(header: Header<PriceHistoryRow, unknown>): string | null {
    if (typeof header.column.columnDef.header === "function") {
      return header.column.columnDef.header(header.getContext());
    }
    if (typeof header.column.columnDef.header === "string") {
      return header.column.columnDef.header;
    }
    return null;
  }

  function renderCellValue(
    cell: Cell<PriceHistoryRow, unknown>
  ): string | PriceHistoryCell["$$props"] | null {
    if (typeof cell.column.columnDef.cell === "function") {
      return cell.column.columnDef.cell?.(cell.getContext());
    }

    return null;
  }

  function renderSubtitleValue(cell: Cell<PriceHistoryRow, unknown>): string | null {
    switch (cell.column.id) {
      case "initialPrice":
        return formatDayMonthByLocale(
          new Date(cell.row.original.initialPrice.invoiceDate),
          $locale
        );
      case "latestPrice":
        return formatDayMonthByLocale(new Date(cell.row.original.latestPrice.invoiceDate), $locale);
    }

    return null;
  }

  function getTitleColor(cell: Cell<PriceHistoryRow, unknown>): string {
    switch (cell.column.id) {
      case "latestPrice":
        return cell.row.original.increase ? "#D11514" : "#006943";
    }

    return "var(--rk-text-dark)";
  }

  function shouldRightAlign(column: ColumnDef<PriceHistoryRow>): boolean {
    return (
      column.id === "initialPrice" || column.id === "latestPrice" || column.id === "periodSpend"
    );
  }

  function edgeClass(index: number): HeaderCell["$$props"]["edge"] {
    if (index === 0) {
      return "start";
    } else if (index === defaultColumns.length - 1) {
      return "end";
    }

    return "middle";
  }

  function onTriggerClick() {
    chatFilterOnTriggerClick();
    isSupplierChatFilterTooltipOpen = false;
    tooltipTimeout = 0;
  }

  $: if (
    $chatFilterCount > 0 &&
    $supplierFilterBadgeElement &&
    tooltipTimeout == null &&
    hasRestoredFilters
  ) {
    isSupplierChatFilterTooltipOpen = true;
    tooltipTimeout = setTimeout(() => {
      isSupplierChatFilterTooltipOpen = false;
    }, 7000);
  }

  onMount(async () => {
    restorePersistedChatFilters(chatFiltersByID);
    hasRestoredFilters = true;
  });

  onDestroy(() => {
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
    }
  });
</script>

<main class="main">
  <PageHeaderNew title={t("prices.header.title")}>
    <div class="left" slot="left-aligned">
      <ButtonGroup>
        <RadioButton isActive={!$changesOnly} on:click={() => changesOnly.set(false)}
          >{t("prices.all.cta")}</RadioButton
        >
        <RadioButton isActive={$changesOnly} on:click={() => changesOnly.set(true)}
          >{t("prices.changesOnly.cta")}</RadioButton
        >
      </ButtonGroup>
      <DatePickerPanel {dateRangeFilters} datePickerStore={globalDatePickerStore} />
    </div>
    <div class="right" slot="right-aligned">
      {#if $selectedBuyer?.id}
        <ButtonNew
          size="xs"
          on:click={handleExportCSVClick}
          isLoading={$exportCSVPriceHistoryMutation.isPending}
          disabled={$priceHistoryQuery.isPending}
          --bg="var(--rk-neutral-4)"
          --minW="76.5px">export</ButtonNew
        >
        <ButtonNew size="xs" on:click={modal.onOpen} bind:element={$triggerElement}
          >{t("prices.priceAlerts.cta")}</ButtonNew
        >
      {/if}
    </div>
  </PageHeaderNew>

  <div
    style:position="relative"
    style:--grid-width={GRID_WIDTH}
    style:--page-header-height="64px"
    style:--col-header-height={COL_HEADER_HEIGHT}
    style:--scrollbar-width={SCROLL_BAR_WIDTH}
    style:--side-menu-width="{$sideMenuWidth}px"
    style:height="100%"
  >
    {#if $priceHistoryQuery.isPlaceholderData}
      <div class="shimmer-loader" />
    {/if}
    <div
      class="main-grid"
      role="grid"
      aria-colcount={defaultColumns.length}
      style:grid-template-columns={columnWidths}
    >
      {#each $table.getHeaderGroups() as headerGroup}
        <div class="header-row">
          {#if $priceHistoryQuery.isLoading}
            {#each defaultColumns as col, index (col.id)}
              <SkeletonColHeader
                colID={col.id ?? ""}
                start={index === 0}
                end={index === defaultColumns.length - 1}
              />
            {/each}
          {:else}
            {#each headerGroup.headers as header, index}
              {@const headerValue = renderHeaderValue(header)}
              {@const rightAlign = shouldRightAlign(header.column.columnDef)}
              {#if header.id === "supplierName"}
                <HeaderCellFilter
                  edge={edgeClass(index)}
                  on:click={onTriggerClick}
                  bind:triggerElement={$chatFilterTriggerElement}
                  filterQueryParam={CHAT_FILTER_PARAM_KEY}
                  badgeElement={supplierFilterBadgeElement}
                  filterCount={$chatFilterCount}
                  filterMenuAction={chatFilterMenu}
                  menuAttrs={$chatFilterMenuAttrs}
                  triggerAttrs={$chatFilterTriggerAttrs}
                  isFilterMenuOpen={$chatFilterOpen}
                  filterMenuItems={chatFilterMenuItems}
                  isHintTooltipVisible={isSupplierChatFilterTooltipOpen}
                  toggleMode="inclusive"
                >
                  {headerValue}
                </HeaderCellFilter>
              {:else}
                <HeaderCell
                  --color="var(--rk-text-transparency-dark)"
                  --hover-color="var(--rk-neutral-4)"
                  --sticky-top="0"
                  --sticky-left="0"
                  align={rightAlign ? "right" : "left"}
                  sortable={header.column.getCanSort()}
                  sortDirection={header.column.getIsSorted()}
                  on:click={header.column.getToggleSortingHandler()}
                  edge={edgeClass(index)}
                >
                  <div class:right-align={rightAlign}>
                    <TextNew font="ocr" size="m" --rk-text-color="var(--rk-text-transparency-dark)"
                      >{headerValue}</TextNew
                    >
                  </div>
                </HeaderCell>
              {/if}
            {/each}
          {/if}
        </div>
      {/each}
      <div class="body-grid">
        {#if $priceHistoryQuery.isLoading}
          {#each skeletonRows as _}
            <PriceHistoryRowSkeleton columns={6} />
          {/each}
        {:else if !isEmptyData}
          {#each rows as row, index (row.id)}
            <div class="row" role="row" tabindex={0}>
              {#each row.getVisibleCells() as cell}
                {@const cellValue = renderCellValue(cell)}
                {@const subtitle = renderSubtitleValue(cell)}
                {@const titleColor = getTitleColor(cell)}
                {@const rightAlign = shouldRightAlign(cell.column.columnDef)}
                <div
                  role="gridcell"
                  in:fade|global={CELL_FADE_BASE_ANIM_CONFIG}
                  out:fade={CELL_FADE_BASE_ANIM_CONFIG}
                  class:right-align={rightAlign}
                >
                  {#if cell.column.id === "hasChange"}
                    {#if cellValue.hasChange}
                      <div class="history-cell">
                        <PriceHistoryCell {...cellValue} />
                      </div>
                    {/if}
                  {:else}
                    <div class="base-cell" class:right-align={rightAlign}>
                      <TextNew size="s" --rk-text-color={titleColor}>{cellValue}</TextNew>
                      {#if subtitle}
                        <TextNew size="s" --rk-text-color="var(--rk-text-low)">{subtitle}</TextNew>
                      {/if}
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
            <div class="row-divider-wrapper">
              <div class="row-divider" />
            </div>
          {/each}
        {/if}
      </div>
    </div>
    {#if isEmptyData}
      <div class="empty-state">
        {#if $priceHistoryQuery.error}
          <EmptyStateBasic title={"Error loading price history"} subtitle={"please try again"} />
        {:else}
          <UploadInvoiceEmptyState
            variant={changesOnly ? "priceHistoryChangesOnly" : "priceHistory"}
          />
        {/if}
      </div>
    {/if}
  </div>
</main>

<PriceChangesEmailModal
  contentAttrs={$contentAttrs}
  underlayAttrs={$underlayAttrs}
  open={$isPriceChangeEmailModalOpen}
  contentAction={modal.content}
  onClose={modal.onClose}
/>

<style>
  .main-grid {
    display: grid;
    padding-bottom: 128px;
    overflow: auto;
    max-width: calc(100vw - var(--side-menu-width) - var(--content-px) * 2);
    max-height: calc(100vh - var(--page-header-height));
  }

  .body-grid {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
  }

  .empty-state {
    width: 100%;
    height: 80vh;
    padding-bottom: 32px;
  }

  .header-row {
    display: contents;
  }

  .row {
    display: contents;
    border-bottom: solid 1px var(--rk-divider-1);
  }

  .base-cell {
    display: flex;
    flex-direction: column;
    padding-left: 12px;
    padding-right: 12px;
  }

  .history-cell {
    width: 100%;
  }

  div[role="gridcell"] {
    display: flex;
    align-items: center;
    min-height: 64px;
  }

  .right-align {
    justify-content: flex-end;
  }

  .right-align * {
    text-align: right;
  }

  .right {
    display: flex;
    gap: 4px;
  }

  .row-divider-wrapper {
    grid-column: 1 / -1;
    background: var(--rk-neutral-0);
  }

  .row-divider {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-color: var(--rk-divider-0);
  }
  .main {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 28px 24px;
    flex: 1 1 auto;
    height: 100%;
  }

  .left {
    display: flex;
    gap: 4px;
  }

  .shimmer-loader {
    position: absolute;
    top: var(--col-header-height);
    width: calc(var(--grid-width) - var(--scrollbar-width));
    bottom: calc(40px + var(--scrollbar-width));
    left: 0;
    overflow-x: hidden;
    z-index: 3;
    pointer-events: none;
  }

  .shimmer-loader::after {
    content: "";
    position: absolute;
    top: 0;
    right: -100%;
    bottom: 0;
    left: -100%;
    background-image: linear-gradient(
      to right,
      rgb(255 255 255 / 0) 33.3%,
      rgb(255 255 255 / 0.8),
      rgb(255 255 255 / 0) 66.6%
    );
    animation: shimmer 1.2s infinite cubic-bezier(0.42, 0, 0.5, 1);
  }

  @keyframes shimmer {
    from {
      transform: translateX(-33.3%);
    }
    to {
      transform: translateX(33.3%);
    }
  }

  :global(.supplier-select) {
    box-sizing: border-box;
    width: 100%;
  }
</style>
